import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Paperclip, X as XIcon, UploadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const TYPES = ["Accounts Receivable","Accounting","Dispatch","Fleet","Service","Safety","Training","Administration","General"];
const PRIORITIES = ["Low","Normal","High","Urgent"];
const MAX_BYTES = 25 * 1024 * 1024; // matches backend cap in tasks_module.add_attachment

function formatBytes(n) {
  if (!Number.isFinite(n) || n <= 0) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  const i = Math.min(u.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}

export default function NewTaskDialog({ onClose, onCreated, prefill = null }) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({
    task_type: prefill?.task_type || "General",
    subject:   prefill?.subject   || "",
    description: prefill?.description || "",
    assigned_to: prefill?.assigned_to || user?.email || "",
    priority:  prefill?.priority  || "Normal",
    due_date:  prefill?.due_date  || "",
  });
  const [files, setFiles] = useState([]); // File[]
  const [saving, setSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  useEffect(() => {
    api.get(`/users`).then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickFiles = (list) => {
    const incoming = Array.from(list || []);
    // De-dupe by name+size so users can re-open the picker without doubling.
    const merged = [...files];
    for (const f of incoming) {
      if (f.size > MAX_BYTES) {
        toast.error(`${f.name} exceeds the 25 MB limit`);
        continue;
      }
      const dup = merged.find((m) => m.name === f.name && m.size === f.size);
      if (!dup) merged.push(f);
    }
    setFiles(merged);
  };

  const removeFile = (idx) => setFiles((fs) => fs.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!form.subject.trim()) {
      toast.error("Subject is required");
      return;
    }
    setSaving(true);
    setUploadProgress({ done: 0, total: files.length });
    try {
      const payload = { ...form };
      if (!payload.due_date) delete payload.due_date;
      // DEC-052 · pass provenance + optional customer through the same POST.
      if (prefill?.customer_account) payload.customer_account = prefill.customer_account;
      if (prefill?.customer_name)    payload.customer_name    = prefill.customer_name;
      if (prefill?.source_type)      payload.source_type      = prefill.source_type;
      if (prefill?.source_id)        payload.source_id        = prefill.source_id;
      if (prefill?.source_ref)       payload.source_ref       = prefill.source_ref;
      const created = await api.post(`/tasks`, payload);
      const taskId = created.data?.id;
      // Attach files serially so we don't hammer the storage backend and
      // so failures are attributable to a specific file. We continue on
      // per-file errors and surface a summary toast at the end.
      let failed = 0;
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fd = new FormData();
        fd.append("file", file);
        try {
          await api.post(`/tasks/${taskId}/attachments`, fd, {
            // Boundary must be auto-set by the browser — same reason as
            // TaskDetailDialog.uploadFile.
            headers: { "Content-Type": undefined },
          });
        } catch (e) {
          failed += 1;
          const msg = e?.response?.data?.detail || e.message;
          toast.error(`${file.name}: ${msg}`);
        }
        setUploadProgress({ done: i + 1, total: files.length });
      }
      if (files.length && failed === 0) {
        toast.success(`Task created with ${files.length} attachment${files.length > 1 ? "s" : ""}`);
      } else if (files.length && failed < files.length) {
        toast.success(`Task created (${files.length - failed} of ${files.length} files attached)`);
      } else {
        toast.success("Task created");
      }
      onCreated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create task");
    } finally {
      setSaving(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="new-task-dialog">
        <DialogHeader>
          <DialogTitle>New Task</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Subject</Label>
            <input
              data-testid="new-task-subject"
              value={form.subject}
              onChange={(e) => set("subject", e.target.value)}
              placeholder="Short summary of the work"
              className="w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type</Label>
              <select value={form.task_type} onChange={(e) => set("task_type", e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm">
                {TYPES.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <Label>Priority</Label>
              <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm">
                {PRIORITIES.map((x) => <option key={x}>{x}</option>)}
              </select>
            </div>
            <div>
              <Label>Assigned To</Label>
              <select data-testid="new-task-assignee" value={form.assigned_to} onChange={(e) => set("assigned_to", e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm">
                {users.map((u) => (
                  <option key={u.email} value={u.email}>{u.full_name} · {u.role}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Due Date</Label>
              <input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm" />
            </div>
          </div>
          <div>
            <Label>Description</Label>
            <textarea
              data-testid="new-task-description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Details, links, context…"
              className="min-h-[90px] w-full rounded border border-gray-300 px-2.5 py-1.5 text-sm"
            />
          </div>

          {/* Attachments — DSG-004 Task Attach-on-Create ------------------ */}
          <div>
            <Label>Attachments (optional)</Label>
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              data-testid="new-task-file-input"
              onChange={(e) => {
                onPickFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <button
              type="button"
              data-testid="new-task-attach-button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <Paperclip size={13} weight="bold" /> Add files
            </button>
            {files.length > 0 && (
              <ul
                className="mt-2 space-y-1 rounded border border-gray-200 bg-gray-50 p-2"
                data-testid="new-task-file-list"
              >
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${f.size}-${i}`}
                    data-testid={`new-task-file-${i}`}
                    className="flex items-center gap-2 text-xs text-gray-700"
                  >
                    <Paperclip size={11} weight="bold" className="shrink-0 text-gray-400" />
                    <span className="min-w-0 flex-1 truncate">{f.name}</span>
                    <span className="text-gray-400">{formatBytes(f.size)}</span>
                    <button
                      type="button"
                      data-testid={`new-task-file-remove-${i}`}
                      onClick={() => removeFile(i)}
                      disabled={saving}
                      className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-gray-700 disabled:opacity-40"
                      title="Remove"
                    >
                      <XIcon size={11} weight="bold" />
                    </button>
                  </li>
                ))}
                <li className="pt-1 text-[10px] text-gray-500">
                  {files.length} file{files.length > 1 ? "s" : ""} · uploaded after the task is created (max 25 MB each)
                </li>
              </ul>
            )}
            {saving && uploadProgress.total > 0 && (
              <div
                className="mt-2 flex items-center gap-1.5 text-xs text-gray-600"
                data-testid="new-task-upload-progress"
              >
                <UploadSimple size={12} weight="bold" className="animate-pulse" />
                Uploading attachments… {uploadProgress.done}/{uploadProgress.total}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-3">
            <button onClick={onClose} disabled={saving} className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
            <button
              data-testid="new-task-submit"
              onClick={submit}
              disabled={saving || !form.subject.trim()}
              className="rounded bg-[#EB1D25] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#AF101B] disabled:opacity-40"
            >
              {saving ? "Creating…" : "Create Task"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Label({ children }) {
  return <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">{children}</div>;
}
